/**
 * Shows banner about subscription status on a team site.
  *
 * Business logic:
 *
 * When a subscription invoice isn’t paid, or credit card payment fails, the
 * subscription is marked in stripe as past due. We should notify relevant users
 * that this has happened and give them a 30-day grace period to pay the invoice or update their
 * credit card info (this grace period is only in stripe, Grist doesn't manage it).
 *
 * During this past due grace period, there will be some banners, and after Stripe decides that subscription
 * should be canceled or marked as unpaid we also show banners - informing that the site is probably in read
 * only mode (if it is not in good standing marked by the server).
  *
 * In read-only mode, users can delete data and documents, but doing so doesn’t restore the
 * subscription. Users can always choose to delete their team site.
 *
 * Notice: this is SAAS only code.
 *
 * Implementation:
 *
 * Depending on the data we have on billingAccount we will show different banners in many variants:
 *
 * Data we have:
 * - stripeStatus in billingAccount object: we only care about "canceled", "past_due", "unpaid"
 * - feature flag "readOnlyDocs" in billingAccount: tells us if the site is in read-only mode
 * - isGoodStanding in billingAccount: tells us if the subscription is in good standing or not, if it is not, we
 *   assume that server put the site into read-only mode (ignoring the readOnlyDocs flag)
 * - if user is owner/editor
 *
 * Some preconditions to show any banner at all:
 * - this must be a team site - for now personal sites are free and even if they are readonly or in bad
 *   shape - we don't show anything (at least not in this banner). We don't check the product, free team sites
 *   also need a inGoodStanding flag to be set to true.
 * - user is not a viewer.
 */

import { Banner, buildBannerMessage } from "app/client/components/Banner";
import { makeT } from "app/client/lib/localization";
import { inlineMarkdown } from "app/client/lib/markdown";
import { AppModel } from "app/client/models/AppModel";
import { logError } from "app/client/models/errors";
import { urlState } from "app/client/models/gristUrlState";

import { Computed, Disposable, dom, DomElementArg, makeTestId } from "grainjs";

const t = makeT("SubscriptionBanners");
const testId = makeTestId("test-subscription-banner-");

// As there are a lot of combinations of states we can be in, we will use a decision table to
// figure out what message to show. Each row in the table correspond to the state we are in.

type Role = "owner" | "editor" | "viewer";
type Place = "site" | "doc";
type Status = "past_due" | "unpaid" | "canceled" | "other"; // special value means some other status
type ReadOnlyMode = boolean;
type InGoodStanding = boolean;

// A DecisionRow will match the current situation if ALL of its specified (non-undefined)
// fields match the current state. Fields that are omitted (undefined) are treated as wildcards and
// match any value. If no message is provided, the matching row acts as a "no banner" rule.
interface DecisionRow {
  role?: Role;
  place?: Place;
  status?: Status;
  readOnlyMode?: ReadOnlyMode;
  inGoodStanding?: InGoodStanding;
  message?(): DomElementArg;
}

// Constants for decision table
const AS_OWNER = "owner" as const;
const AS_EDITOR = "editor" as const;
const AS_VIEWER = "viewer" as const;
const SITE = "site" as const;
const DOC = "doc" as const;

// Decision rules are static, but messages depend on billingUrl and days, so we make a function
// that creates them (to support translations mainly as we need text near the `t` function call).
//
// Notice that the component will short-circuit and won't exercise those rules if everything is ok.
export function getDecisionRules(billingUrl: string, days: number): DecisionRow[] {
  return [
    // No banner rules

    // Viewer's don't see any banners at all.
    {
      role: AS_VIEWER,
    },
    // If site looks ok, no need for a banner
    {
      status: "other",
      readOnlyMode: false,
      inGoodStanding: true,
    },

    // Banner rules - if we are here then we will show some banner if we are on a team site.

    // OWNER cases, they see banners both on site and doc pages
    // past-due but still active both site and doc level
    {
      role: AS_OWNER,
      status: "past_due",
      inGoodStanding: true,
      message() {
        return inlineMarkdown(t(
          "Your team's subscription plan is past due. Please [pay the latest invoice]({{billingUrl}}) \
           to maintain an active subscription. Your team site will become read-only in {{days}} days.",
          { billingUrl, days },
        ));
      },
    },

    // same as above but the subscription is not in good standing anymore, maybe it was switched manually
    {
      role: AS_OWNER,
      status: "past_due",
      inGoodStanding: false,
      message() {
        return inlineMarkdown(t(
          "Your team's subscription plan is past due. \
           Please [pay the latest invoice]({{billingUrl}}) to reactivate your subscription.",
          { billingUrl },
        ));
      },
    },

    // Unpaid status for owners
    {
      role: AS_OWNER,
      status: "unpaid",
      message() {
        return inlineMarkdown(t(
          "Your team's subscription plan is more than 30-days past due. \
           To reactivate your subscription, please [pay the latest invoice]({{billingUrl}}).",
          { billingUrl },
        ));
      },
    },

    // Cancel status for owners, same as above but without the days part, as the subscription is already canceled.
    {
      role: AS_OWNER,
      status: "canceled",
      message() {
        return inlineMarkdown(t(
          "Your team's subscription plan has been cancelled. \
           To reactivate your subscription, please [renew your plan]({{billingUrl}}).",
          { billingUrl },
        ));
      },
    },

    // If we switched the flag manually for other reasons, inform the owner everywhere about that.
    {
      role: AS_OWNER,
      inGoodStanding: false,
      message() {
        return t("Your site's subscription is not in good standing. Please contact support for assistance.");
      },
    },

    // If site is limited by the feature flag, inform the owner on the site only level, as this we don't have a
    // use case for it yet (but it is possible to do it manually).
    {
      role: AS_OWNER,
      place: SITE,
      readOnlyMode: true,
      message() {
        return t("Your site is in read-only mode. Please contact support for assistance.");
      },
    },

    // EDITOR cases
    // past-due status on doc page
    {
      role: AS_EDITOR,
      status: "past_due",
      message() {
        return t("Your team's subscription plan is past due. Please contact the site owner to update payment.");
      },
    },

    // unpaid status on doc page (read-only mode)
    {
      role: AS_EDITOR,
      status: "unpaid",
      message() {
        return t(
          "Your team's subscription plan is more than 30-days past due. \
           Please contact the site owner to update payment and reactivate your subscription.",
        );
      },
    },

    // canceled status on doc page (read-only mode)
    {
      role: AS_EDITOR,
      status: "canceled",
      message() {
        return t(
          "Your team's subscription plan has been cancelled. \
           Please contact the site owner to reactivate your subscription.",
        );
      },
    },

    // catch-all for any other bad state on doc pages (not in good standing)
    {
      role: AS_EDITOR,
      place: DOC,
      inGoodStanding: false,
      message() {
        return t(
          "Your site's subscription is not in good standing. \
           Please contact the site owner for assistance.",
        );
      },
    },
  ];
}

// Banner shown if the site subscription is not in a good state.
export class SubscriptionBanner extends Disposable {
  private _onSite = Computed.create(this, this._app.pageType, (use, pageType) => {
    return pageType !== "doc";
  });

  constructor(private _app: AppModel) {
    super();
  }

  public buildDom() {
    // Only for team sites
    if (!this._app.isTeamSite || !this._app.currentOrg?.billingAccount) {
      return null;
    }

    const isViewer = !this._app.isOwnerOrEditor();

    // Viewers don't see subscription banners at all, so short circuit here.
    if (isViewer) {
      return null;
    }

    const billingAccount = this._app.currentOrg.billingAccount;

    // Now figure out what we know about the subscription and team site.
    const isOwner = this._app.isOwner(); // otherwise editor
    const status = this._app.currentOrg.billingAccount.status?.stripeStatus as "past_due" | "unpaid" |
      "canceled" | undefined;
    const readOnlyMode = this._app.currentOrg.billingAccount.features?.readOnlyDocs;
    const inGoodStanding = this._app.currentOrg.billingAccount.inGoodStanding;

    // Short-circuit: if everything is good, no banner.
    const watchedStatus = ["past_due", "unpaid", "canceled"];
    if (!watchedStatus.includes(status || "") && inGoodStanding && !readOnlyMode) {
      return null;
    }

    // URL to billing page
    const billingUrl = urlState().makeUrl({ billing: "billing" });
    // How many days until site goes into read-only mode (for past_due status), we assume that 30
    // days after end of current period (this is hardcoded this information is only in stripe).
    const endOfPeriod = billingAccount.status?.currentPeriodEnd;
    const endOfPeriodPlus30Days = endOfPeriod ? movedDate(date(endOfPeriod), 30) : null;
    const daysCalculated = endOfPeriodPlus30Days ? dayDiff(endOfPeriodPlus30Days, date(new Date())) : 30;
    const days = daysCalculated > 0 ? daysCalculated : 0; // don't show negative days, should not happen.

    return dom.domComputed(this._onSite, (onSite) => {
      // Make a decision tree and find the right message to show (see comments above).
      const decisionTree = getDecisionRules(billingUrl, days);

      // Figure out which state we are in.
      const state = {
        role: isOwner ? AS_OWNER : AS_EDITOR,
        place: onSite ? SITE : DOC,
        status: watchedStatus.includes(status || "") ? status : "other",
        readOnlyMode: readOnlyMode ?? false,
        inGoodStanding: inGoodStanding ?? true,
      };

      // And find out what to show based on the decision tree.
      const matchingRow = decisionTree.find((row) => {
        return (
          matches(row.role, state.role) &&
          matches(row.place, state.place) &&
          matches(row.status, state.status) &&
          matches(row.readOnlyMode, state.readOnlyMode) &&
          matches(row.inGoodStanding, state.inGoodStanding)
        );
      });

      if (!matchingRow) {
        // This should not happen, but maybe there are new stripe status that we don't know about, so lets
        // not show any banner, but log this case so we can add it to the decision tree.
        logError(`SubscriptionBanner: no matching row found for state ${JSON.stringify(state)}`);
        return null;
      }

      const message = matchingRow?.message?.();

      // Decide the style of the banner here, just based on the status, it is red by default unless past_due.
      const style = (status === "past_due") ? "warning" : "error";

      if (message) {
        return dom.create(Banner, {
          content: buildBannerMessage(
            message,
            testId("text"),
            hidden(status, testId("status")),
            hidden(state.readOnlyMode, testId("readonly")),
            hidden(state.inGoodStanding, testId("good-standing")),
            hidden(days, testId("days")),
            hidden(style, testId("style")),
          ),
          style,
          showCloseButton: false,
        });
      } else {
        return null;
      }
    });
  }
}

// Helper function to create hidden input with value, used for testing to easily get the state of the banner.
function hidden(value: any, ...args: DomElementArg[]) {
  return dom("input", { type: "hidden", value: String(value) }, ...args);
}

// If flag is set returns true if value matches flag, if flag is not set, returns true (matches any value).
function matches<T>(flag: T | undefined, value: T) {
  if (flag === undefined) {
    return true;
  }
  return flag === value;
}

// Some date helpers to avoid the need of importing moment to this bundle.
function date(d: string | Date): Date {
  const clone = ((d instanceof Date) ? new Date(d.getTime()) : new Date(d));
  clone.setUTCHours(0, 0, 0, 0);
  return clone;
}

function movedDate(d: Date, days: number): Date {
  const result = new Date(d);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

function dayDiff(date1: Date, date2: Date): number {
  return Math.floor((date1.getTime() - date2.getTime()) / (24 * 60 * 60 * 1000));
}
